import React, { useState, useMemo, useEffect } from "react";
import { Form, Input, DatePicker, Row, Col, Collapse, FormInstance, Select } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
    StepContainer,
    Title,
    AddOptionButton,
    StyledCollapse,
    StyledPanel,
    CollapseHeader,
    HeaderInfo,
    StyledPlusIcon,
    StyledMinusIcon,
    DeleteButton,
    ShipAddressContainer,
} from "./styles";
import "react-phone-input-2/lib/style.css";
import { getCountries } from "../../../../redux/features/generaleWebData/generaleWebDataAsyncThunk";
import { ActivationStatus } from "../../../../types";
import { useAppDispatch, useAppSelector } from "../../../../utils/hooks/redux";
import Dropdown from "../../../../components/Dropdown";
import CustomInput from "../../../../components/Input";
import { sortOptionsAlphabetically, sortOptionWithTopValues, topCountriesId } from "../../../../utils/helpers/sortOptionsAlphabetically";
import { selectShipFromPort } from "../../../../redux/features/ShipFromPort/ShipFromPortSlice";
import { Label } from "../../../../components/Input/styles";
import { StyledPhoneInput } from "../../Modal/styles";
import { convertUtcToLocal } from "../../../../utils/helpers/convertUtcToLocal";
import { OtherShipPort } from "../../../../types/ShipFromPortTypes";
import { canAddMultipleShippingOption, enableCutOffDate } from "../../../../utils/constants";
import { countries } from "../../../../utils/data/countries";
import GoogleAutocompleteInput, { Place } from "../../../../components/GoogleAutocomplete";

const MAX_OPTIONS = 3;

interface QuestionPanelProps {
    optionNumber: number;
    form: FormInstance;
    optionKey: string;
    countriesStatus: string;
}

const QuestionPanel: React.FC<QuestionPanelProps> = ({ optionNumber, form, optionKey, countriesStatus }) => {
    const [placeId, setPlaceId] = useState<string>("");
    const { data: shipFromPortsData, loading: shipFromPortLoading } = useAppSelector(selectShipFromPort);
    const selectedShipFromPort = Form.useWatch([optionKey, "shipFromPort"], form);
    const existingId = Form.useWatch([optionKey, "id"], form);
    const shipFromId = Form.useWatch([optionKey, "shipFrom"], form);
    const region = Form.useWatch([optionKey, "region"], form);

    const shipFromPortsOptions = useMemo(() => {
        const allOptions = [
            ...(shipFromPortsData?.map((ShipFromPort) => ({
                label: ShipFromPort.port + " " + ShipFromPort.contactName,
                value: ShipFromPort.id.toString(),
                key: ShipFromPort.id.toString(),
                status: ShipFromPort.status,
                disabled: ShipFromPort.status !== ActivationStatus.Active,
            })) || []),
            {
                label: "Other",
                value: OtherShipPort,
                key: OtherShipPort,
                status: ActivationStatus.Active,
                disabled: false,
            },
        ].filter((shipFormPort: any) => shipFormPort?.status === ActivationStatus.Active);

        // Sort alphabetically with "Other" at the end
        return sortOptionsAlphabetically(allOptions, "label");
    }, [shipFromPortsData]);

    const renderCountryOptions = useMemo(() => {
        if (countries?.length > 0) {
            const sortedCountries = sortOptionWithTopValues(countries, "name", topCountriesId);
            return sortedCountries.map((country) => ({
                key: country.code,
                value: country.code,
                label: country.name,
            }));
        }
        return [];
    }, [countries]);

    const tempPlaceId = useMemo(() => {
        setPlaceId(form.getFieldValue([optionKey, "tempPlaceId"]));
        return form.getFieldValue([optionKey, "tempPlaceId"]);
    }, [form.getFieldValue([optionKey, "tempPlaceId"])]);

    const handleCountryChange = (value: string) => {
        form.setFieldsValue({
            [optionKey]: {
                ...form.getFieldValue(optionKey),
                countryCode: value,
                region: value,
                city: undefined,
                state: undefined,
                zip: undefined,
                address1: undefined,
                address2: undefined,
                placeId: undefined,
                tempPlaceId: undefined,
                searchAddress: undefined,
            },
        });
        setPlaceId("");
    };

    const handleSelectShipFromPort = (value: string) => {
        if (value === OtherShipPort) {
            form.setFieldsValue({
                [optionKey]: {
                    ...form.getFieldValue(optionKey),
                    contactName: undefined,
                    country: undefined,
                    state: undefined,
                    city: undefined,
                    zip: undefined,
                    address1: undefined,
                    address2: undefined,
                    email: undefined,
                    phone: undefined,
                    port: undefined,
                    comment: undefined,
                    shipFrom: undefined,
                    countryCode: undefined,
                    stateCode: undefined,
                    placeId: undefined,
                    tempPlaceId: undefined,
                    region: undefined,
                    searchAddress: undefined,
                },
            });
            setPlaceId("");
        } else {
            form.setFieldsValue({
                [optionKey]: {
                    shipFrom: value,
                    ...form.getFieldValue(optionKey),
                },
            });
        }
    };

    const handlePlace = (place: Place | null) => {
        if (!place) return;
        form.setFieldsValue({
            [optionKey]: {
                ...form.getFieldValue(optionKey),
                address1: place.address1,
                address2: "",
                city: place.city,
                state: place.state,
                country: place.country,
                zip: place.postalCode,
                stateCode: place.stateCode,
                countryCode: place.countryCode,
                placeId: place.placeId,
                region: place.countryCode,
            },
        });
        setPlaceId(place.placeId);
    };

    useEffect(() => {
        if (selectedShipFromPort !== OtherShipPort) {
            const findSelectedShipFromPort = shipFromPortsData.find((ShipFromPort) => ShipFromPort.id.toString() === selectedShipFromPort);
            if (findSelectedShipFromPort?.id) {
                form.setFieldsValue({
                    [optionKey]: {
                        ...form.getFieldValue(optionKey),
                        contactName: findSelectedShipFromPort.contactName,
                        country: findSelectedShipFromPort.country,
                        state: findSelectedShipFromPort.state,
                        city: findSelectedShipFromPort.city,
                        address1: findSelectedShipFromPort.address1,
                        address2: findSelectedShipFromPort.address2,
                        zip: findSelectedShipFromPort.zip,
                        email: findSelectedShipFromPort.email,
                        phone: findSelectedShipFromPort.phone,
                        comment: findSelectedShipFromPort.comment,
                        port: findSelectedShipFromPort.port,
                        countryCode: findSelectedShipFromPort.countryCode,
                        stateCode: findSelectedShipFromPort.stateCode,
                        placeId: findSelectedShipFromPort.placeId,
                        tempPlaceId: findSelectedShipFromPort.placeId,
                        region: findSelectedShipFromPort.countryCode,
                    },
                });
                setPlaceId(findSelectedShipFromPort.placeId || "");
            }
        }
    }, [selectedShipFromPort, optionKey, shipFromPortsData]);

    useEffect(() => {
        if (existingId && !shipFromId) {
            form.setFieldsValue({
                [optionKey]: {
                    ...form.getFieldValue(optionKey),
                    shipFromPort: OtherShipPort,
                },
            });
        }
    }, [shipFromId, existingId]);

    const isOtherShipFromPort = useMemo(() => selectedShipFromPort === OtherShipPort, [selectedShipFromPort]);

    // Filter out the "Other" option for our check
    const validShipFromPorts = useMemo(() => {
        return shipFromPortsOptions.filter(option => option.value !== OtherShipPort);
    }, [shipFromPortsOptions]);

    // Auto-select the only available port if there's exactly one
    useEffect(() => {
        if (validShipFromPorts.length === 1 && !form.getFieldValue([optionKey, 'shipFromPort'])) {
            const onlyPort = validShipFromPorts[0];
            form.setFieldsValue({
                [optionKey]: {
                    ...form.getFieldValue(optionKey),
                    shipFromPort: onlyPort.value
                }
            });
            // Also trigger the select handler to populate other fields
            handleSelectShipFromPort(onlyPort.value);
        }
    }, [validShipFromPorts.length, optionKey]);

    return (
        <>
            <CustomInput label="" fontWeight="" placeholder="" name={[optionKey, "port"]} hidden />
            <CustomInput label="" fontWeight="" placeholder="" name={[optionKey, "comment"]} hidden />
            <CustomInput label="" fontWeight="" placeholder="" name={[optionKey, "shipFrom"]} hidden />
            <CustomInput label="" fontWeight="" placeholder="" name={[optionKey, "id"]} hidden />
            <Form.Item name={[optionKey, "country"]} hidden />
            <Form.Item name={[optionKey, "countryCode"]} hidden />
            <Form.Item name={[optionKey, "stateCode"]} hidden />
            <Form.Item name={[optionKey, "placeId"]} hidden />
            <Form.Item name={[optionKey, "tempPlaceId"]} hidden />

            {enableCutOffDate && <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                <Col xs={24}>
                    <Form.Item name={[optionKey, "cutOffDate"]} rules={[{ required: enableCutOffDate, message: "Please select cut-off date" }]} label={<span style={{ fontWeight: "700" }}>Cut-off Date</span>}>
                        <DatePicker style={{ width: "100%" }} size="large" placeholder="MM/DD/YYYY" />
                    </Form.Item>
                </Col>
            </Row>}

            <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                <Col xs={24}>
                    <Dropdown
                        fontWeight="700"
                        label={"Ship From Port"}
                        name={[optionKey, "shipFromPort"]}
                        loading={shipFromPortLoading}
                        options={shipFromPortsOptions}
                        rules={[
                            {
                                required: true,
                                message: "Please select ship from port",
                            },
                        ]}
                        onSelect={handleSelectShipFromPort}
                    />
                </Col>
            </Row>

            <ShipAddressContainer style={{ display: isOtherShipFromPort ? "block" : "none" }}>
                <div style={{ fontWeight: "400", marginBottom: "16px" }}>Ship From Address</div>
                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24}>
                        <Form.Item name={[optionKey, "contactName"]} rules={[{ required: true, message: "Please enter name" }]} label={<span style={{ fontWeight: "700" }}>Name</span>}>
                            <Input size="large" placeholder="Name" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24} sm={24} md={12}>
                        <CustomInput
                            fontWeight="400"
                            label={"Email"}
                            placeholder="Email"
                            name={[optionKey, "email"]}
                            rules={[{ required: false, message: "Please enter valid email", type: "email" }]}
                            onChange={(e: any) => {
                                form.setFields([
                                    {
                                        name: [optionKey, "email"],
                                        value: e.target.value?.trim()?.toLowerCase(),
                                    },
                                ]);
                            }}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item label={<Label fontWeight="400">Phone</Label>} name={[optionKey, "phone"]} rules={[{ required: false, message: "Please enter phone number" }]}>
                            <StyledPhoneInput
                                country={"us"}
                                inputProps={{
                                    placeholder: "Enter a Phone Number",
                                }}
                                inputStyle={{
                                    width: "100%",
                                    padding: "10px",
                                    borderRadius: "3px",
                                    fontSize: "15px",
                                    paddingLeft: "40px",
                                    background: "#fff",
                                    height: "40px",
                                    fontWeight: "400",
                                    color: "#333333",
                                }}
                                buttonStyle={{
                                    borderRight: "none",
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24}>
                        <Dropdown
                            fontWeight="700"
                            label={"Country"}
                            name={[optionKey, "region"]}
                            loading={countriesStatus === "loading"}
                            options={renderCountryOptions}
                            rules={[
                                {
                                    required: true,
                                    message: "Please select country",
                                },
                            ]}
                            onSelect={handleCountryChange}
                        />
                    </Col>
                </Row>

                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24}>
                        <GoogleAutocompleteInput
                            label="Search Address"
                            placeholder="Search Address"
                            // googleMapsOptions={{ region: form?.getFieldValue([optionKey, "region"]) }}
                            googleMapsOptions={{
                                autocompleteOptions: {
                                    includedRegionCodes: [region],
                                },
                                // debounce: 2000,
                                minCharsForSuggestions: 1, // Minimum characters before fetching
                            }}
                            onPlaceSelected={handlePlace}
                            placeId={tempPlaceId}
                            name={[optionKey, "searchAddress"]}
                            form={form}
                        />
                    </Col>
                </Row>

                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24}>
                        <Form.Item name={[optionKey, "address1"]} rules={[{ required: true, message: "Please enter address" }]} label={<span style={{ fontWeight: "700" }}>Address 1</span>}>
                            <Input size="large" placeholder="Address 1" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24}>
                        <Form.Item name={[optionKey, "address2"]} label={<span style={{ fontWeight: "400" }}>Address 2</span>}>
                            <Input size="large" placeholder="Address 2" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24} sm={24} md={12}>
                        <CustomInput
                            fontWeight="700"
                            label={"State"}
                            placeholder="State"
                            name={[optionKey, "state"]}
                            rules={[{ required: true, message: "Please enter state" }]}
                        />
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <CustomInput
                            fontWeight="700"
                            label={"City"}
                            placeholder="City"
                            name={[optionKey, "city"]}
                            rules={[{ required: true, message: "Please enter city" }]}
                        />
                    </Col>

                </Row>
                <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                    <Col xs={24} sm={24} md={12}>
                        <CustomInput
                            fontWeight="400"
                            label={"Zip Code"}
                            placeholder="Zip Code"
                            name={[optionKey, "zip"]}
                        />
                    </Col>
                    {/* <Col xs={24} sm={24} md={12}>
                        <CustomInput
                            fontWeight="700"
                            label={"Country"}
                            placeholder="Country"
                            name={[optionKey, "country"]}
                            disabled={true}
                            rules={[{ required: true, message: "Please select country" }]}
                        />
                    </Col> */}
                </Row>
            </ShipAddressContainer>

            <Row gutter={[24, { xs: 16, sm: 24, md: 24 }]}>
                <Col xs={24}>
                    <Form.Item name={[optionKey, "description"]} label={<span style={{ fontWeight: "400" }}>Additional Info</span>}>
                        <Input.TextArea rows={4} placeholder="Additional Info" />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
};

interface Step2Props {
    form: FormInstance;
    data: Record<string, any>;
}

const Step2: React.FC<Step2Props> = ({ form, data }) => {
    // Initialize state based on existing form data
    const initialOptionsCount = useMemo(() => {
        // Count how many options already exist in the form data
        let count = 0;
        for (let i = 1; i <= MAX_OPTIONS; i++) {
            if (data[`option${i}`]) {
                count = i;
            }
        }
        return Math.max(count, 1); // At least 1 option
    }, [data]);

    const [activeKey, setActiveKey] = useState<string | string[]>(Array.from({ length: initialOptionsCount }, (_, i) => (i + 1).toString()));
    const [optionsCount, setOptionsCount] = useState(initialOptionsCount);
    const dispatch = useAppDispatch();

    const { countryState } = useAppSelector((state) => state.generaleWebData);
    const { status: countriesStatus } = useMemo(() => countryState, [countryState]);

    const addNewOption = () => {
        if (optionsCount < MAX_OPTIONS) {
            const newOptionNumber = optionsCount + 1;
            setOptionsCount(newOptionNumber);
            setActiveKey([...Array.from(activeKey), newOptionNumber.toString()]);
        }
    };

    const handleDeleteOption = (optionNumber: number) => {
        // Prevent deleting if only one option remains
        if (optionsCount <= 1) return;

        const newOptionsCount = optionsCount - 1;
        setOptionsCount(newOptionsCount);

        // Get current form values
        const values = form.getFieldsValue();
        const newValues: Record<string, any> = {};

        // Reconstruct options by shifting up all options after the deleted one
        for (let i = 1; i <= MAX_OPTIONS; i++) {
            if (i < optionNumber) {
                // Keep options before the deleted one unchanged
                newValues[`option${i}`] = values[`option${i}`];
            } else if (i <= newOptionsCount) {
                // Shift up the remaining options
                newValues[`option${i}`] = values[`option${i + 1}`];
            } else {
                // Clear any remaining options
                newValues[`option${i}`] = undefined;
            }
        }

        // Update form values and active keys
        form.setFieldsValue(newValues);
        setActiveKey(Array.from({ length: newOptionsCount }, (_, i) => (i + 1).toString()));
    };

    // Create a HeaderWatch component to isolate re-renders
    const CollapseHeaderWatch: React.FC<{ optionNumber: number }> = ({ optionNumber }) => {
        const optionKey = `option${optionNumber}`;

        // Watch only the fields needed for the header
        const cutOffDate = Form.useWatch([optionKey, "cutOffDate"], form);
        const contactName = Form.useWatch([optionKey, "contactName"], form);
        const countryId = Form.useWatch([optionKey, "country"], form);
        const countryName = Form.useWatch([optionKey, "country"], form);

        return (
            <CollapseHeader>
                <HeaderInfo>
                    {canAddMultipleShippingOption && <span className="option-number">Option #{optionNumber}</span>}
                    {enableCutOffDate && <span className="date-info">{cutOffDate ? convertUtcToLocal(cutOffDate, "MM/DD/YYYY") : "MM/DD/YYYY"}</span>}
                    <span className="contact-info">{contactName || "Contact Name"}</span>
                    <span className="country-info">{countryName || "Country"}</span>
                </HeaderInfo>
                {optionsCount > 1 && (
                    <DeleteButton
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteOption(optionNumber);
                        }}
                    >
                        <DeleteOutlined />
                    </DeleteButton>
                )}
            </CollapseHeader>
        );
    };

    // Memoize the panel header to prevent unnecessary re-renders
    const renderPanelHeader = useMemo(() => (optionNumber: number) => <CollapseHeaderWatch optionNumber={optionNumber} />, [form, optionsCount]);

    const expandIcon = ({ isActive }: { isActive?: boolean }) => {
        return isActive ? <StyledMinusIcon /> : <StyledPlusIcon />;
    };

    const renderOption = (optionNumber: number) => {
        const optionKey = `option${optionNumber}`;

        return (
            <StyledPanel header={renderPanelHeader(optionNumber)} key={optionNumber.toString()}>
                <QuestionPanel optionNumber={optionNumber} form={form} optionKey={optionKey} countriesStatus={countriesStatus} />
            </StyledPanel>
        );
    };

    return (
        <StepContainer>
            <Title>Ship From</Title>
            <Form form={form} layout="vertical">
                <StyledCollapse activeKey={activeKey} onChange={(key) => setActiveKey(key)} expandIcon={expandIcon}>
                    {Array.from({ length: optionsCount }, (_, i) => renderOption(i + 1))}
                </StyledCollapse>
                {canAddMultipleShippingOption && <AddOptionButton type="primary" onClick={addNewOption} icon={<PlusOutlined />} disabled={optionsCount >= MAX_OPTIONS}>
                    Add Another Ship From Option
                </AddOptionButton>}
            </Form>
        </StepContainer>
    );
};

export default Step2;
